import R from "react"
import { useNavigate } from "react-router-dom"
import * as P from "../program-state"
import * as C from "../navigation"
import * as Controls from "../controls"
import { dashboard_overview_t, dashboard_network_health_t, dashboard_top_performing_t } from "../resource/types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFacebook, faLinkedin, faTiktok } from "@fortawesome/free-brands-svg-icons"
import { faExclamationTriangle, faCheckCircle, faSpinner } from "@fortawesome/free-solid-svg-icons"

type dashboard_state = {
    overview: dashboard_overview_t | null
    network_health: dashboard_network_health_t | null
    top_performing: dashboard_top_performing_t | null
    loading: boolean
    error: string | null
}

const Dashboard_content = () => {
    const [ps, cs] = P.use_authenticated_program_state()
    const navigate = useNavigate()
    const [state, set_state] = R.useState<dashboard_state>({
        overview: null,
        network_health: null,
        top_performing: null,
        loading: true,
        error: null
    })

    R.useEffect(() => {
        const load_dashboard_data = async () => {
            set_state(prev => ({ ...prev, loading: true, error: null }))

            const [overview_ok, overview_data] = await P.fetch_dashboard_overview(cs, 30)
            const [health_ok, health_data] = await P.fetch_network_health(cs)
            const [top_ok, top_data] = await P.fetch_top_performing(cs, 'conversions', 5, 30)

            if (!overview_ok || !health_ok || !top_ok) {
                set_state(prev => ({ ...prev, loading: false, error: "Failed to load dashboard data" }))
                return
            }

            set_state({
                overview: overview_data,
                network_health: health_data,
                top_performing: top_data,
                loading: false,
                error: null
            })
        }

        load_dashboard_data()
    }, [cs])

    if (state.loading) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
                <FontAwesomeIcon icon={faSpinner} spin className="tw-text-4xl tw-text-blue-600" />
            </div>
        )
    }

    if (state.error) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
                <div className="tw-text-red-600 tw-text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="tw-text-4xl tw-mb-4" />
                    <p>{state.error}</p>
                </div>
            </div>
        )
    }

    const overview = state.overview!
    const network_health = state.network_health!
    const top_performing = state.top_performing!

    const has_data = overview.total_active_campaigns > 0

    const get_network_icon = (network: string) => {
        switch (network.toLowerCase()) {
            case 'facebook':
            case 'meta':
                return faFacebook
            case 'linkedin':
                return faLinkedin
            case 'tiktok':
                return faTiktok
            default:
                return faCheckCircle
        }
    }

    const format_currency = (amount: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
    }

    const format_number = (num: number) => {
        return new Intl.NumberFormat('en-US').format(num)
    }

    return (
        <div className="tw-space-y-6">
            <div className="tw-flex tw-items-center tw-justify-between">
                <h1 className="tw-text-3xl tw-font-bold">Dashboard</h1>
            </div>

            {!has_data ? (
                <Controls.Eres_card>
                    <div className="tw-p-8 tw-text-center">
                        <p className="tw-text-lg tw-text-gray-600 tw-mb-4">Welcome! You don't have any campaigns yet.</p>
                        <p className="tw-text-gray-500 tw-mb-6">Complete the onboarding process to connect your social media accounts, create a brand, and launch your first campaign.</p>
                        <Controls.Eres_button onClick={() => navigate('/onboarding')}>
                            Start onboarding
                        </Controls.Eres_button>
                    </div>
                </Controls.Eres_card>
            ) : (
                <>
                    {/* stats cards */}
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4">
                        <Controls.Eres_card>
                            <div className="tw-p-4">
                                <div className="tw-text-sm tw-text-gray-500">Active Campaigns</div>
                                <div className="tw-text-2xl tw-font-bold tw-mt-1">{overview.total_active_campaigns}</div>
                            </div>
                        </Controls.Eres_card>
                        <Controls.Eres_card>
                            <div className="tw-p-4">
                                <div className="tw-text-sm tw-text-gray-500">Total Spend (30d)</div>
                                <div className="tw-text-2xl tw-font-bold tw-mt-1">{format_currency(overview.total_spend_30d)}</div>
                            </div>
                        </Controls.Eres_card>
                        <Controls.Eres_card>
                            <div className="tw-p-4">
                                <div className="tw-text-sm tw-text-gray-500">Impressions (30d)</div>
                                <div className="tw-text-2xl tw-font-bold tw-mt-1">{format_number(overview.total_impressions_30d)}</div>
                            </div>
                        </Controls.Eres_card>
                        <Controls.Eres_card>
                            <div className="tw-p-4">
                                <div className="tw-text-sm tw-text-gray-500">Clicks (30d)</div>
                                <div className="tw-text-2xl tw-font-bold tw-mt-1">{format_number(overview.total_clicks_30d)}</div>
                            </div>
                        </Controls.Eres_card>
                        <Controls.Eres_card>
                            <div className="tw-p-4">
                                <div className="tw-text-sm tw-text-gray-500">Conversions (30d)</div>
                                <div className="tw-text-2xl tw-font-bold tw-mt-1">{format_number(overview.total_conversions_30d)}</div>
                            </div>
                        </Controls.Eres_card>
                    </div>

                    {/* network breakdown */}
                    <Controls.Eres_card>
                        <div className="tw-p-6">
                            <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Network Breakdown (30d)</h2>
                            <div className="tw-space-y-4">
                                {overview.network_stats.map((stat) => (
                                    <div key={stat.network} className="tw-flex tw-items-center tw-justify-between">
                                        <div className="tw-flex tw-items-center tw-gap-3">
                                            <FontAwesomeIcon
                                                icon={get_network_icon(stat.network)}
                                                className="tw-text-xl"
                                            />
                                            <div>
                                                <div className="tw-font-medium">{stat.network}</div>
                                                <div className="tw-text-sm tw-text-gray-500">
                                                    {stat.active_campaigns} campaigns
                                                </div>
                                            </div>
                                        </div>
                                        <div className="tw-text-right">
                                            <div className="tw-font-semibold">{format_currency(stat.total_spend)}</div>
                                            <div className="tw-text-sm tw-text-gray-500">
                                                {format_number(stat.conversions)} conv
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Controls.Eres_card>

                    {/* network health status */}
                    <Controls.Eres_card>
                        <div className="tw-p-6">
                            <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Network Health Status</h2>
                            <div className="tw-space-y-3">
                                {network_health.map((network, idx) => {
                                    const is_healthy = network.is_connected && !network.needs_reauth
                                    const needs_attention = network.is_connected && network.needs_reauth
                                    return (
                                        <div key={network.ad_account_id || `${network.network}-${idx}`} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded">
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                <FontAwesomeIcon
                                                    icon={get_network_icon(network.network)}
                                                    className="tw-text-xl"
                                                />
                                                <div>
                                                    <div className="tw-font-medium">{network.network}</div>
                                                    {network.ad_account_name && (
                                                        <div className="tw-text-sm tw-text-gray-600">{network.ad_account_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                {is_healthy ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-text-big-stone-rlydark" />
                                                        <span className="tw-text-big-stone-rlydark tw-font-medium">Connected</span>
                                                    </>
                                                ) : needs_attention ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="tw-text-yellow-600" />
                                                        <span className="tw-text-yellow-600 tw-font-medium">Reconnect Required</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="tw-text-red-600" />
                                                        <span className="tw-text-red-600 tw-font-medium">Not Connected</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </Controls.Eres_card>

                    {/* recent campaigns */}
                    {/* @TODO: add a link to the campaign page */}
                    <Controls.Eres_card>
                        <div className="tw-p-6">
                            <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Recent Campaigns</h2>
                            <div className="tw-overflow-x-auto">
                                <table className="tw-w-full">
                                    <thead>
                                        <tr className="tw-border-b tw-border-gray-200">
                                            <th className="tw-text-left tw-pb-3 tw-font-semibold">Campaign</th>
                                            <th className="tw-text-left tw-pb-3 tw-font-semibold">Network</th>
                                            <th className="tw-text-left tw-pb-3 tw-font-semibold">Status</th>
                                            <th className="tw-text-right tw-pb-3 tw-font-semibold">Spend</th>
                                            <th className="tw-text-right tw-pb-3 tw-font-semibold">Impressions</th>
                                            <th className="tw-text-right tw-pb-3 tw-font-semibold">Conversions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overview.recent_campaigns.map((campaign) => (
                                            <tr key={campaign.id} className="tw-border-b tw-border-gray-100">
                                                <td className="tw-py-3">{campaign.name}</td>
                                                <td className="tw-py-3">
                                                    <div className="tw-flex tw-items-center tw-gap-1">
                                                        {campaign.networks.map((network, idx) => (
                                                            <FontAwesomeIcon
                                                                key={`${campaign.id}-${network}-${idx}`}
                                                                icon={get_network_icon(network)}
                                                                className="tw-text-sm"
                                                                title={network}
                                                            />
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="tw-py-3">
                                                    <span className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${
                                                        campaign.status === 'active' ? 'tw-bg-green-100 tw-text-green-800' :
                                                        campaign.status === 'paused' ? 'tw-bg-yellow-100 tw-text-yellow-800' :
                                                        'tw-bg-gray-100 tw-text-gray-800'
                                                    }`}>
                                                        {campaign.status}
                                                    </span>
                                                </td>
                                                <td className="tw-py-3 tw-text-right">{format_currency(campaign.total_spend)}</td>
                                                <td className="tw-py-3 tw-text-right">{format_number(campaign.impressions)}</td>
                                                <td className="tw-py-3 tw-text-right">{format_number(campaign.conversions)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Controls.Eres_card>
                </>
            )}
        </div>
    )
}

export default () => {
    return <C.Emit_main page_id="PAGE-dashboard"><Dashboard_content /></C.Emit_main>
}
